import { useCallback, useMemo } from 'react'

import { runInAction, toJS } from 'mobx'

import { UploadLogFilterType } from '~/client/graph'
import {
  useDeleteUploadLogCustomFilterMutation,
  useGetUploadLogCustomFiltersByProjectQuery,
  useSaveUploadLogCustomFilterMutation,
} from '~/client/graph/operations/generated/CustomUploadLogFilters.generated'
import {
  DeleteFilterMutationFn,
  FilterTypeDescriptor,
  SaveFilterMutationFn,
} from '~/client/src/desktop/components/SavedViews'
import { attachmentsGroupingOptionList } from '~/client/src/shared/enums/AttachmentsGroupingOption'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { IFilters } from '~/client/src/shared/stores/InitialState'
import { matchLocationKeysToOptions } from '~/client/src/shared/utils/logisticsHelper'

import { getAttachmentsGroupingOptionTranslatorKey } from '../components/AttachmentsHeaderBar/AttachmentsHeaderBar'
import AttachmentFilterStore from '../components/AttachmentsHeaderBar/components/AttachmentsFilters/AttachmentFilter.store'

interface UseAttachmentsSavedViewsParams {
  projectId: string
  attachmentFilterStore: AttachmentFilterStore
  filters: IFilters & any
  facets: FilterTypeDescriptor[]
}

export const useAttachmentsSavedViews = ({
  projectId,
  attachmentFilterStore,
  filters,
  facets,
}: UseAttachmentsSavedViewsParams) => {
  const { data: savedViewsData } = useGetUploadLogCustomFiltersByProjectQuery({
    variables: { projectId },
    skip: !projectId,
  })

  const savedViews = useMemo(() => {
    return (
      savedViewsData?.uploadLogCustomFilters?.data?.map(filter => ({
        id: filter.id,
        name: filter.name,
        isPublic: filter.isPublic,
        filtersByFilterType: filter.filtersByFilterType,
        groupBy: filter.groupBy,
        userId: filter.userId,
      })) || []
    )
  }, [savedViewsData])

  const appliedFilters = useMemo(() => {
    const filtersMap: { [key: string]: Set<string> } = {}
    const fieldsMap = toJS(filters.fieldsMap)

    Object.entries(fieldsMap || {}).forEach(([filterType, filterInfo]) => {
      const info = filterInfo as any
      if (info.initialFilterOptions?.size > 0) {
        const filterValues = Array.from(
          info.initialFilterOptions.keys(),
        ) as string[]
        if (filterValues.length > 0) {
          filtersMap[filterType] = new Set(filterValues)
        }
      } else if (info.appliedFilterOptions?.length > 0) {
        filtersMap[filterType] = new Set(info.appliedFilterOptions)
      }
    })

    return filtersMap
  }, [toJS(filters.fieldsMap)])

  const currentGrouping = useMemo(() => {
    return filters.groupingKey || ''
  }, [filters.groupingKey])

  const [saveUploadLogFilter] = useSaveUploadLogCustomFilterMutation()
  const [deleteUploadLogFilter] = useDeleteUploadLogCustomFilterMutation()

  const saveFilterMutation: SaveFilterMutationFn = useCallback(
    async options => saveUploadLogFilter(options as any),
    [saveUploadLogFilter],
  )

  const deleteFilterMutation: DeleteFilterMutationFn = useCallback(
    async options => deleteUploadLogFilter(options as any),
    [deleteUploadLogFilter],
  )

  const handleApplyFilters = useCallback(
    (filtersToApply: { [key: string]: Set<string> }, groupBy: string) => {
      runInAction(() => {
        if (groupBy) {
          filters.groupingKey = groupBy
        }

        const stores = attachmentFilterStore.filterStoresByTypeMap

        Object.values(stores).forEach(filterStore => {
          filterStore.clickOnDeselectAll()
          filterStore.clickOnApply()
        })

        Object.entries(filtersToApply || {}).forEach(([type, valueSet]) => {
          if (filters.fieldsMap[type]) {
            filters.fieldsMap[type].selectedItemsIds = Array.from(valueSet)
          }

          const filterStore = stores[type]
          if (!filterStore) {
            return
          }

          const savedKeys = Array.from(valueSet)
          // A saved `Kind|id` location key keeps the kind it was saved with: re-match
          // it to the live option by id, or a re-kind drops it from the view (STX1-597)
          filterStore.selectOptionsByIds(
            type === UploadLogFilterType.Location
              ? matchLocationKeysToOptions(savedKeys, filterStore.allOptionIds)
              : savedKeys,
          )
          filterStore.clickOnApply()
        })

        attachmentFilterStore.syncFilters()
      })
    },
    [attachmentFilterStore, filters],
  )

  const filteredFacets = useMemo((): FilterTypeDescriptor[] => {
    const availableKeys = Object.keys(filters.fieldsMap || {})
    return facets.filter(facet => availableKeys.includes(facet.key))
  }, [facets, filters.fieldsMap])

  const groupingOptions = useMemo(() => {
    return attachmentsGroupingOptionList.map(option => ({
      key: option,
      label:
        Localization.translator[
          getAttachmentsGroupingOptionTranslatorKey(option)
        ],
    }))
  }, [])

  return {
    savedViews,
    saveFilterMutation,
    deleteFilterMutation,
    handleApplyFilters,
    appliedFilters,
    currentGrouping,
    facets: filteredFacets,
    groupingOptions,
  }
}
